import { createLogic } from "redux-logic";
import type { Action } from "redux";
import { type FinishConfigSaveType, CONFIGURATION_FINISH_SAVE, startConfigSave } from "../actions/SaveActions";
import {
    type BuyCarType,
    type LeaseCarType,
    type ReserveCarType,
    type SetWaitForBuyActionsType,
    BUY_CAR,
    BUY_WAIT_FOR_ACTIONS,
    buyCar,
    hideOverlayLoader,
    LEASE_CAR,
    RESERVE_CAR,
    setWaitForBuyActions,
    showOverlayLoader,
} from "../../../../../common-deprecated/redux/actions/UtilActions";
import { type FinishFinanceRateFetchType, FINISH_FINANCE_RATE_FETCH } from "../actions/FinanceInsuranceActions";
import { INIT_CAR } from "../actions/CarActions";
import type { BuildAndBuyLogicType } from "../store";
import { SET_GRADE } from "../actions/GradeActions";
import { startCheckoutFlow } from "../../../../shared-logic/utils/checkoutUtils";
import { setEnableBuy, setEnableLease, setEnableReserve } from "../actions/BnbSettingsActions";

// Validation before going to the buy step.
const buyCarLogic = (createLogic as BuildAndBuyLogicType<BuyCarType>)({
    type: BUY_CAR,

    validate({ getState, action }, allow, reject) {
        const { finance, save } = getState();

        if (finance.fetching || save.saving || !save.saved) {
            // Wait with validation logic until async actions required for buy validation are done.
            return reject(setWaitForBuyActions(true));
        }

        allow(action);
    },

    process({ getState }, dispatch, done) {
        const { save, commonSettings } = getState();
        startCheckoutFlow(save, commonSettings);
        done();
    },
});

const reserveCarLogic = (createLogic as BuildAndBuyLogicType<ReserveCarType>)({
    type: RESERVE_CAR,

    validate({ getState, action }, allow, reject) {
        const { finance, save } = getState();

        if (finance.fetching || save.saving || !save.saved) {
            // Wait with validation logic until async actions required for buy validation are done.
            return reject(setWaitForBuyActions(true));
        }

        allow(action);
    },

    process({ getState }, dispatch, done) {
        const { save, commonSettings } = getState();
        startCheckoutFlow(save, commonSettings, true);
        done();
    },
});

const leaseCarLogic = (createLogic as BuildAndBuyLogicType<LeaseCarType>)({
    type: LEASE_CAR,

    validate({ getState, action }, allow, reject) {
        const { finance, save } = getState();

        if (finance.fetching || save.saving || !save.saved) {
            return reject(setWaitForBuyActions(true));
        }

        allow(action);
    },

    process({ getState }, dispatch, done) {
        const { save, commonSettings } = getState();
        startCheckoutFlow(save, commonSettings);
        done();
    },
});

const buyWaitLogic = (createLogic as BuildAndBuyLogicType<SetWaitForBuyActionsType>)({
    type: BUY_WAIT_FOR_ACTIONS,
    process({ getState }, dispatch, done) {
        const { save } = getState();
        dispatch(showOverlayLoader());

        // Monthly rate should be up to date or fetching, but it can be possible the configuration is not saved at all.
        // For example: User clicks on Step 4 in customize (= not saved yet)
        if (!save.saved) dispatch(startConfigSave());

        done();
    },
});

type WaitActionType = FinishFinanceRateFetchType | FinishConfigSaveType;
const buyWaitActionLogic = (createLogic as BuildAndBuyLogicType<WaitActionType>)({
    type: [FINISH_FINANCE_RATE_FETCH, CONFIGURATION_FINISH_SAVE],

    process({ getState }, dispatch, done) {
        const { finance, save, utils } = getState();

        if (utils.waitingForBuyActions && !finance.error && !finance.fetching && !save.saving && save.saved) {
            dispatch(hideOverlayLoader());
            dispatch(buyCar());
        }

        done();
    },
});

const enableBuyReserveStepLogic = (createLogic as BuildAndBuyLogicType<Action>)({
    type: INIT_CAR,
    process({ getState }, dispatch, done) {
        const { car, bnbSettings } = getState();
        if (bnbSettings.enableBuy) dispatch(setEnableBuy(car.availableForOnlineSales));
        if (bnbSettings.enableReserve) dispatch(setEnableReserve(car.availableForOnlineReservation));
        if (bnbSettings.enableLeasing) dispatch(setEnableLease(car.availableForOnlineLeasing));

        done();
    },
});

const enableBuyReserveStepOnGradeLogic = (createLogic as BuildAndBuyLogicType<Action>)({
    type: SET_GRADE,
    process({ getState }, dispatch, done) {
        const { grade, bnbSettings } = getState();
        if (bnbSettings.enableBuy) dispatch(setEnableBuy(grade.availableForOnlineSales));
        if (bnbSettings.enableReserve) dispatch(setEnableReserve(grade.availableForOnlineReservation));
        if (bnbSettings.enableLeasing) dispatch(setEnableLease(grade.availableForOnlineLeasing));

        done();
    },
});
export default [
    buyCarLogic,
    reserveCarLogic,
    leaseCarLogic,
    buyWaitLogic,
    buyWaitActionLogic,
    enableBuyReserveStepLogic,
];
export const serverLogic = [enableBuyReserveStepLogic, enableBuyReserveStepOnGradeLogic];
